import React, { useState, useEffect, useCallback } from 'react';
import { reportService, configService } from '../services/api';

const AdminPanel = ({ adminPassword }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteVoteThreshold, setDeleteVoteThreshold] = useState(5);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await configService.get(adminPassword);
      setDeleteVoteThreshold(data.delete_vote_threshold || 5);
    } catch (error) {
      console.error('Error fetching config:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Invalid admin password.');
      }
    }
  }, [adminPassword]);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportService.getAll(adminPassword);
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      if (error.response?.status === 401) {
        setError('Authentication failed. Invalid admin password.');
      } else if (error.response?.status === 500) {
        setError('Server configuration error. Please contact administrator.');
      } else {
        setError('Failed to load reports. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchReports();
    fetchConfig();
  }, [fetchReports, fetchConfig]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReports();
    setRefreshing(false);
  };

  const handleApprove = async (reportId) => {
    if (!window.confirm('⚠️ Are you sure you want to approve this report and DELETE the place? This action cannot be undone.')) {
      return;
    }
    
    setActionLoading(reportId);
    try {
      await reportService.approve(reportId, adminPassword);
      
      // Refresh reports from server to get updated data
      await fetchReports();
      
    } catch (error) {
      console.error('Error approving report:', error);
      if (error.response?.status === 401) {
        alert('❌ Authentication failed. Please refresh and log in again.');
      } else {
        alert('❌ Failed to approve report. Please try again.');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (reportId) => {
    if (!window.confirm('Are you sure you want to reject this report?')) {
      return;
    }
    
    setActionLoading(reportId);
    try {
      await reportService.reject(reportId, adminPassword);
      
      // Refresh reports from server
      await fetchReports();
      
    } catch (error) {
      console.error('Error rejecting report:', error);
      if (error.response?.status === 401) {
        alert('❌ Authentication failed. Please refresh and log in again.');
      } else {
        alert('❌ Failed to reject report. Please try again.');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateConfig = async () => {
    setConfigLoading(true);
    try {
      await configService.update({ delete_vote_threshold: deleteVoteThreshold }, adminPassword);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch (error) {
      console.error('Error updating config:', error);
      if (error.response?.status === 401) {
        alert('❌ Authentication failed. Please refresh and log in again.');
      } else {
        alert('Failed to update configuration');
      }
    } finally {
      setConfigLoading(false);
    }
  };

  const filteredReports = reports.filter((r) => r.status === filter);
  const pendingCount = reports.filter(r => r.status === 'pending').length;
  const approvedCount = reports.filter(r => r.status === 'approved').length;
  const rejectedCount = reports.filter(r => r.status === 'rejected').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchReports}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8 pb-24">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center space-x-3">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Admin Panel</span>
              </h1>
              <p className="text-gray-600">Manage reported places and system configuration</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="mt-4 md:mt-0 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>System Configuration</span>
          </h2>
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delete Vote Threshold
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Number of community votes required before a place is automatically deleted
              </p>
              <input
                type="number"
                min="1"
                max="20"
                value={deleteVoteThreshold}
                onChange={(e) => setDeleteVoteThreshold(parseInt(e.target.value))}
                className="w-full md:w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            <button
              onClick={handleUpdateConfig}
              disabled={configLoading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
            >
              {configLoading ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
          {configSaved && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-green-700 font-medium">Configuration saved successfully!</span>
            </div>
          )}
        </div>

        {/* Reports Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Reported Places</h2>

          {/* Tabs */}
          <div className="flex space-x-1 mb-6 border-b border-gray-200 overflow-x-auto">
            {[
              { key: 'pending', label: 'Pending', count: pendingCount, color: 'yellow' },
              { key: 'approved', label: 'Approved', count: approvedCount, color: 'green' },
              { key: 'rejected', label: 'Rejected', count: rejectedCount, color: 'red' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-6 py-3 font-medium transition-all whitespace-nowrap ${
                  filter === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center space-x-2">
                  <span>{tab.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    tab.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    tab.color === 'green' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {tab.count}
                  </span>
                </span>
              </button>
            ))}
          </div>

          {/* Reports List */}
          {filteredReports.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg">No {filter} reports</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReports.map(report => (
                <div
                  key={report.id}
                  className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                    <div className="flex-1 mb-4 md:mb-0">
                      {/* Status Badge */}
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          report.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : report.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {report.status.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500">
                          Report #{report.id}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(report.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Reason */}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">Reason:</p>
                        <p className="text-gray-900 font-medium">{report.reason}</p>
                      </div>

                      {/* Place Details */}
                      {report.place ? (
                        <div className="bg-white rounded-lg p-4 space-y-2">
                          <p className="text-sm"><span className="font-medium text-gray-700">Place:</span> <span className="text-gray-900">{report.place.description}</span></p>
                          <p className="text-sm"><span className="font-medium text-gray-700">Location:</span> <span className="text-gray-600">{Number(report.place.lat).toFixed(4)}, {Number(report.place.lng).toFixed(4)}</span></p>
                          <p className="text-sm"><span className="font-medium text-gray-700">Rating:</span> <span className="text-yellow-500">{'★'.repeat(Math.round(report.place.rating))}{'☆'.repeat(5 - Math.round(report.place.rating))}</span></p>
                          {report.place.moods && report.place.moods.length > 0 && (
                            <p className="text-sm"><span className="font-medium text-gray-700">Moods:</span> {Array.isArray(report.place.moods) ? report.place.moods.join(', ') : report.place.moods}</p>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-100 rounded-lg p-4">
                          <p className="text-sm text-gray-500 italic">Place has been deleted</p>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {report.status === 'pending' && (
                      <div className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-2 md:ml-6">
                        <button
                          onClick={() => handleApprove(report.id)}
                          disabled={actionLoading === report.id}
                          className="flex-1 md:flex-none px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1 whitespace-nowrap"
                        >
                          {actionLoading === report.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span>Approve & Delete</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleReject(report.id)}
                          disabled={actionLoading === report.id}
                          className="flex-1 md:flex-none px-5 py-2.5 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-900 rounded-lg text-sm font-medium transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
